import type { DateTime } from "luxon";
import type { Department } from "../../contexts/AuthProvider";
import type { DepartmentLocation, NurseFullRole } from "../../utils/models";
import { getJobDisplayName } from "../../utils/roles";
import { convertLocationToTimeSlot } from "../dailyPrintout/phases/timeSlotOps";
import type { ScheduleRow, TimeSlot } from "../dailyPrintout/types";
import { weekdayAbbreviation } from "../dailyPrintout/util/locations";
import { type AiPrintoutOrders, type AiPrintoutTimeContext, jobRankOf, rankIn, roleFields, textOrNull, timeFields } from "./aiPrintoutRowMappers";
import type { TimeGroupKeysOf } from "./aiPrintoutTimeGroups";
import type { AiPrintoutGap, AiPrintoutLabels, AiPrintoutRequirement } from "./contract";

/** The gap finder's wildcard for a slot any role can fill. */
const ANY_ROLE = "all";

/** A required position: a department location's census-adjusted requirement, placed on the day. */
export type RequiredSlot = { location: DepartmentLocation; slot: TimeSlot };

/**
 * The positions the gap finder measures the day against (`findLocationsWithNoNurse`): the Detailed source rows'
 * census-adjusted locations that name a location, run this weekday and need someone, each placed on the schedule
 * day by `convertLocationToTimeSlot`.
 */
export const requiredSlotsOf = (locations: DepartmentLocation[], date: DateTime, department: Department): RequiredSlot[] => {
	const weekdayAbbr = weekdayAbbreviation(date);
	return locations
		.filter((location) => location.location !== "" && location.days.includes(weekdayAbbr) && location.amountRequired > 0)
		.map((location) => {
			const trimmed = { ...location, location: location.location.trim() };
			return { location: trimmed, slot: convertLocationToTimeSlot(trimmed, date, department.timezone, department.dayStart) };
		});
};

/** The role a slot needs, or null when any role fills it. */
export const slotRoleOf = (slot: TimeSlot): NurseFullRole | null => {
	const slotRole = slot.roles[0];
	return slotRole === ANY_ROLE ? null : slotRole;
};

interface SlotFieldsArgs {
	slot: TimeSlot;
	department: Department;
	labels: AiPrintoutLabels;
	context: AiPrintoutTimeContext;
	orders: AiPrintoutOrders;
	timeGroupKeysOf: TimeGroupKeysOf;
}

/** What a gap and a required position share: where, which role and job, when, and in which time groups. */
const slotFields = ({ slot, department, labels, context, orders, timeGroupKeysOf }: SlotFieldsArgs) => {
	const role = slotRoleOf(slot);
	return {
		location: slot.location,
		...(role === null ? { role, roleLabel: labels.anyRole, roleShort: labels.anyRole } : roleFields(role)),
		jobId: slot.jobId,
		jobName: slot.jobId === null ? null : getJobDisplayName(slot.jobId, department),
		locationRank: rankIn(orders.locationRanks, slot.location),
		roleRank: role === null ? null : rankIn(orders.roleRanks, role),
		jobRank: jobRankOf(slot.jobId, department),
		...timeFields(slot.start, slot.end, context),
		timeGroupKeys: timeGroupKeysOf(slot.start, slot.end),
	};
};

/** An unfilled position (a gap finder slot) with the floor of its source row. */
export const mapGap = (args: SlotFieldsArgs & { row: ScheduleRow }): AiPrintoutGap => ({ ...slotFields(args), floor: textOrNull(args.row.floor) });

export const mapRequirement = (args: Omit<SlotFieldsArgs, "slot"> & { required: RequiredSlot }): AiPrintoutRequirement => {
	const { location, slot } = args.required;
	return {
		...slotFields({ ...args, slot }),
		floor: textOrNull(location.floor),
		required: location.amountRequired,
	};
};
